import { Box, Paper, Typography } from "@mui/material";
import React from "react";
import { useStyle } from "../LeaveManagement/leaveStyles";
import {
  formatAmountLeftDisplay,
  formatAmountPaidDisplay,
  formatRs,
  isLoanApproved,
} from "./loanRequestUtils";

const labelSx = { fontWeight: 500, fontSize: "16px", color: "#253766" };
const valueSx = {
  fontWeight: 400,
  fontSize: "14px",
  color: "#253766",
  textAlign: "right",
};

const invoiceLineSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 2,
  px: 2,
  py: 1.25,
  borderBottom: "1px solid #e8eef5",
};

const InvoiceLine = ({ label, subLabel, value, valueColor }) => (
  <Box sx={invoiceLineSx}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={labelSx}>{label}</Typography>
      {subLabel ? (
        <Typography
          component="span"
          display="block"
          sx={{
            fontSize: "11px",
            fontWeight: 400,
            color: "text.secondary",
            mt: 0.25,
          }}
        >
          {subLabel}
        </Typography>
      ) : null}
    </Box>
    <Typography
      sx={{
        ...valueSx,
        flexShrink: 0,
        ...(valueColor ? { color: valueColor } : {}),
      }}
    >
      {value}
    </Typography>
  </Box>
);

const LoanSummaryCard = ({ loan }) => {
  const classes = useStyle();

  if (!loan) {
    return null;
  }

  const approved = isLoanApproved(loan.hr_status);

  return (
    <Paper
      className={classes.sectionsContainer}
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box className={classes.sectionHeader}>Loan Summary</Box>
      {approved ? (
        <Box
          sx={{
            mx: 3,
            mt: 2,
            mb: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              border: "1px solid #dce4ee",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#fff",
              flexShrink: 0,
            }}
          >
            <InvoiceLine label="Original Amount" value={formatRs(loan.amount)} />
            <InvoiceLine
              label="Service Charge"
              subLabel="(one time payment)"
              value={formatRs(loan.service_charge)}
            />
            <Box
              sx={{
                ...invoiceLineSx,
                borderBottom: "2px solid #253766",
                bgcolor: "#f3f8fe",
                py: 1.5,
              }}
            >
              <Typography sx={{ ...labelSx, fontWeight: 600 }}>
                Total Payable
              </Typography>
              <Typography sx={{ ...valueSx, fontWeight: 600 }}>
                {formatRs(loan.total_payable)}
              </Typography>
            </Box>
            <InvoiceLine
              label="Total Amount Paid"
              value={formatAmountPaidDisplay(loan.amount_paid, loan.hr_status)}
              valueColor="success.main"
            />
            <Box
              sx={{
                ...invoiceLineSx,
                borderBottom: "none",
                bgcolor: "#fff8f8",
                borderTop: "1px dashed #e8eef5",
                py: 1.5,
              }}
            >
              <Typography sx={{ ...labelSx, fontWeight: 600 }}>
                Total Amount Left
              </Typography>
              <Typography
                sx={{ ...valueSx, fontWeight: 600, color: "error.main" }}
              >
                {formatAmountLeftDisplay(loan.amount_left, loan.hr_status)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }} aria-hidden />
        </Box>
      ) : (
        <Box sx={{ flex: 1 }} aria-hidden />
      )}
    </Paper>
  );
};

export default LoanSummaryCard;
